import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Input, Divider } from '@attentive-platform/stem-ui';

import { useRosettaStore } from '../../../Stores/useRosettaStore';

const CompanyVolumeCard = () => {
  const {
    companyName,
    projectsPerYear,
    sheetsPerProject,
    totalAnnualSheets,
    perTradeDistribution,
    selectedTrades,
    tradesData,
    dispatch
  } = useRosettaStore(
    useShallow(state => ({
      companyName: state.companyName,
      projectsPerYear: state.projectsPerYear,
      sheetsPerProject: state.sheetsPerProject,
      totalAnnualSheets: state.totalAnnualSheets,
      perTradeDistribution: state.perTradeDistribution,
      selectedTrades: state.selectedTrades,
      tradesData: state.tradesData,
      dispatch: state.dispatch
    }))
  );

  const tradesMap = useMemo(() => {
    const map = new Map();
    tradesData.forEach(trade => map.set(trade.trade_id, trade));
    return map;
  }, [tradesData]);

  const sumOfTradeSheets = useMemo(() => {
    return Array.from(selectedTrades).reduce((sum, tradeId) => {
      return sum + (perTradeDistribution[tradeId] || 0);
    }, 0);
  }, [selectedTrades, perTradeDistribution]);

  const isDistributionValid = sumOfTradeSheets === totalAnnualSheets;

  const prevTotalSheetsRef = useRef(totalAnnualSheets);
  const prevSelectedTradesRef = useRef(selectedTrades);

  useEffect(() => {
    const totalChanged = prevTotalSheetsRef.current !== totalAnnualSheets;
    const tradesChanged =
      prevSelectedTradesRef.current.size !== selectedTrades.size ||
      Array.from(prevSelectedTradesRef.current).some(id => !selectedTrades.has(id));

    if (selectedTrades.size > 0 && totalAnnualSheets > 0 && (totalChanged || tradesChanged)) {
      const equalDistribution = Math.floor(totalAnnualSheets / selectedTrades.size);
      const remainder = totalAnnualSheets % selectedTrades.size;

      const newDistribution = {};
      const tradeIds = Array.from(selectedTrades);

      tradeIds.forEach((tradeId, index) => {
        // Distribute remainder to first few trades
        newDistribution[tradeId] = equalDistribution + (index < remainder ? 1 : 0);
      });

      dispatch({ type: 'SET_PER_TRADE_DISTRIBUTION', payload: newDistribution });
    }

    prevTotalSheetsRef.current = totalAnnualSheets;
    prevSelectedTradesRef.current = selectedTrades;
  }, [totalAnnualSheets, selectedTrades, dispatch]);

  const handleCompanyNameChange = useCallback(
    e => {
      dispatch({ type: 'SET_COMPANY_NAME', payload: e.target.value });
    },
    [dispatch]
  );

  const handleProjectsPerYearChange = useCallback(
    e => {
      const newProjectsPerYear = parseInt(e.target.value) || 0;
      dispatch({ type: 'SET_PROJECTS_PER_YEAR', payload: newProjectsPerYear });
      const calculatedTotal = newProjectsPerYear * sheetsPerProject;
      dispatch({ type: 'SET_TOTAL_ANNUAL_SHEETS', payload: calculatedTotal });
    },
    [dispatch, sheetsPerProject]
  );

  const handleSheetsPerProjectChange = useCallback(
    e => {
      const newSheetsPerProject = parseInt(e.target.value) || 0;
      dispatch({ type: 'SET_SHEETS_PER_PROJECT', payload: newSheetsPerProject });
      const calculatedTotal = projectsPerYear * newSheetsPerProject;
      dispatch({ type: 'SET_TOTAL_ANNUAL_SHEETS', payload: calculatedTotal });
    },
    [dispatch, projectsPerYear]
  );

  const handleTotalAnnualSheetsChange = useCallback(
    e => {
      dispatch({ type: 'SET_TOTAL_ANNUAL_SHEETS', payload: parseInt(e.target.value) || 0 });
    },
    [dispatch]
  );

  const handlePerTradeDistributionChange = useCallback(
    (tradeId, e) => {
      const value = parseInt(e.target.value) || 0;
      dispatch({
        type: 'SET_PER_TRADE_DISTRIBUTION',
        payload: {
          ...perTradeDistribution,
          [tradeId]: value
        }
      });
    },
    [dispatch, perTradeDistribution]
  );

  return (
    <section className='rosetta-pricing-card'>
      <h2 className='rosetta-pricing-title'>Company Information & Volume</h2>

      <div className='rosetta-form-group'>
        <label className='rosetta-form-label'>Company Name</label>
        <Input
          type='text'
          value={companyName}
          onChange={handleCompanyNameChange}
          placeholder='Enter your company name'
          size='small'
          className='w-full'
        />
      </div>

      <div className='rosetta-form-group'>
        <label className='rosetta-form-label'>Annual Sheet Estimation</label>
        <div className='rosetta-volume-input-group'>
          <div className='rosetta-volume-calc'>
            <Input type='number' value={projectsPerYear} onChange={handleProjectsPerYearChange} min='0' size='small' />
            <span className='rosetta-input-label'>Projects/Year</span>
          </div>
          <span className='rosetta-operator'>×</span>
          <div className='rosetta-volume-calc'>
            <Input
              type='number'
              value={sheetsPerProject}
              onChange={handleSheetsPerProjectChange}
              min='0'
              size='small'
            />
            <span className='rosetta-input-label'>Sheets/Project</span>
          </div>
          <span className='rosetta-operator'>=</span>
          <div className='rosetta-volume-calc'>
            <Input
              type='number'
              value={totalAnnualSheets}
              onChange={handleTotalAnnualSheetsChange}
              min='0'
              className='total-input'
              size='small'
            />
            <span className='rosetta-input-label'>Total Sheets</span>
          </div>
        </div>
        <p className='text-xs'>Calculate from projects or enter total directly</p>
        <Divider />
      </div>

      {/* Sheets per Trade */}
      <div className='rosetta-distribution-container'>
        <h4 className='rosetta-distribution-subtitle'>Sheets per Trade</h4>
        <div className='rosetta-distribution-grid'>
          {selectedTrades.size > 0
            ? Array.from(selectedTrades).map(tradeId => {
                const trade = tradesMap.get(tradeId);
                return (
                  <div key={tradeId} className='rosetta-distribution-item'>
                    <label className='rosetta-distribution-label'>{trade?.trade_name || ''}</label>
                    <Input
                      type='number'
                      placeholder='0'
                      min='0'
                      value={perTradeDistribution[tradeId] || ''}
                      onChange={e => handlePerTradeDistributionChange(tradeId, e)}
                      size='small'
                    />
                  </div>
                );
              })
            : 'No trades selected'}
        </div>

        {/* Validation Warning */}
        {!isDistributionValid && selectedTrades.size > 0 && (
          <div className='rosetta-validation-warning'>
            <p className='text-sm text-red-600'>
              ⚠️ Please adjust the sheets per trade so that the sum ({sumOfTradeSheets}) is equal to total sheets (
              {totalAnnualSheets})
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CompanyVolumeCard;
